import { useEffect, useRef, useState } from 'react';
import { ExportFormat, Info } from '@ais-ui/websockets';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { AsyncJob, AsyncJobStatus, useAsyncJobs } from '@ais-ui/async-jobs';

/**
 * Default world bounds for export when map context is not available.
 * These bounds cover the entire world.
 */
const WORLD_BOUNDS = {
  center: { lat: 0, lng: 0 },
  zoomLevel: 2,
  northEast: { lat: 90, lng: 180 },
  southWest: { lat: -90, lng: -180 },
};

interface UseExportReportSimpleReturn {
  onExport: (format: ExportFormat) => void;
  activeExportFormat: ExportFormat | null;
  exportJobs: AsyncJob[];
}

/**
 * A simplified version of useExportReport that doesn't require map context.
 * Uses world bounds by default, suitable for exporting from report item menus.
 */
export const useExportReportSimple = (
  jobId: string,
  reportInfo?: Info | undefined
): UseExportReportSimpleReturn => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const [activeExportFormat, setActiveExportFormat] =
    useState<ExportFormat | null>(null);

  // Use our AsyncJobs framework
  const { startJob, jobs } = useAsyncJobs();

  // Filter to get only export related jobs for this report
  const exportJobs = jobs.filter(
    (job) =>
      job.metadata?.type === 'report-export' && job.metadata?.reportId === jobId
  );

  // Get the active job if any
  const activeJob = exportJobs.find(
    (job) =>
      job.status === AsyncJobStatus.PENDING ||
      job.status === AsyncJobStatus.RUNNING
  );

  // Track already notified jobs with a ref
  const notifiedJobsRef = useRef(new Set());

  // When job status changes
  useEffect(() => {
    // Set activeExportFormat based on any running job
    if (activeJob) {
      setActiveExportFormat(activeJob.metadata?.format);
    } else {
      setActiveExportFormat(null);
    }

    // Look for recently completed jobs to show toast notifications
    const recentlyCompletedJob = exportJobs.find(
      (job) =>
        (job.status === AsyncJobStatus.COMPLETED ||
          job.status === AsyncJobStatus.FAILED) &&
        job.completedAt &&
        new Date().getTime() - job.completedAt.getTime() < 3000 &&
        !notifiedJobsRef.current.has(job.id)
    );

    if (recentlyCompletedJob) {
      notifiedJobsRef.current.add(recentlyCompletedJob.id);

      if (recentlyCompletedJob.status === AsyncJobStatus.COMPLETED) {
        toast({
          className: 'export-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDetailsExportReportSuccessMessage,
        });
      } else {
        toast({
          className: 'export-report-error-toast',
          title: messages.genericToastTitleError,
          description: messages.reportsDetailsExportReportErrorMessage,
          variant: 'destructive',
        });
      }
    }
  }, [exportJobs, activeJob, toast, messages]);

  const onExport = (format: ExportFormat) => {
    // Show initial toast
    toast({
      title: messages.genericToastTitleInfo,
      description: messages.reportsExportStartMessages,
      variant: 'info',
    });

    // Set local state
    setActiveExportFormat(format);

    const { center, zoomLevel, northEast, southWest } = WORLD_BOUNDS;

    startJob({
      name: `Export Report (${format})`,
      executor: async (): Promise<unknown> => {
        return new Promise<unknown>(() => {
          // This promise intentionally never resolves on its own
          // The ReportExportManager handles the actual export
        });
      },
      metadata: {
        type: 'report-export',
        reportId: jobId,
        format,
        reportInfo,
        centerLat: center.lat,
        centerLon: center.lng,
        zoomLevel: Math.round(zoomLevel),
        minLat: southWest.lat,
        minLon: southWest.lng,
        maxLat: northEast.lat,
        maxLon: northEast.lng,
      },
    });
  };

  return {
    onExport,
    activeExportFormat,
    exportJobs,
  };
};
